import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// VARIANT SYSTEM — 8 variants, all premium, full dark/light support
// Uses semantic tokens from tailwind.config.js (CSS variable backed)
// ─────────────────────────────────────────────────────────────────────────────
const VARIANTS = {

  // 1. UNDERLINE — sleek bottom border accent ───────────────────────────────
  underlined: {
    wrapper: "border-b border-border",
    list: "gap-0",
    tab: (active) =>
      [
        "relative px-4 pb-3 pt-2.5 border-b-2 font-medium transition-all duration-200 rounded-none",
        active
          ? "border-primary text-primary"
          : "border-transparent text-text_secondary hover:text-text_primary hover:border-border_strong",
      ].join(" "),
    badgeActive: "bg-primary text-white",
    badgeInactive: "bg-surface_alt text-text_secondary",
    dotActive: "bg-primary",
  },

  // 2. PILLS — floating card on tinted tray ─────────────────────────────────
  pills: {
    wrapper: "bg-surface_alt/80 p-1 rounded-xl",
    list: "gap-1",
    tab: (active) =>
      [
        "relative px-4 py-2 font-medium rounded-lg transition-all duration-200",
        active
          ? "bg-surface text-text_primary shadow-[0_1px_3px_rgba(0,0,0,0.10),0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-border"
          : "text-text_secondary hover:text-text_primary hover:bg-surface/70",
      ].join(" "),
    badgeActive: "bg-primary text-white",
    badgeInactive: "bg-border text-text_secondary",
    dotActive: "bg-primary",
  },

  // 3. SEGMENTED — solid color fill, iOS-style ──────────────────────────────
  segmented: {
    wrapper: "border border-border rounded-xl p-1 bg-surface shadow-sm",
    list: "gap-0.5",
    tab: (active) =>
      [
        "relative px-4 py-2 font-medium rounded-lg transition-all duration-200",
        active
          ? "bg-primary text-white shadow-sm"
          : "text-text_secondary hover:text-text_primary hover:bg-surface_alt",
      ].join(" "),
    badgeActive: "bg-white/25 text-white",
    badgeInactive: "bg-surface_alt text-text_secondary",
    dotActive: "bg-white",
  },

  // 4. ELEVATED — card tabs with top-edge accent stripe ─────────────────────
  elevated: {
    wrapper: "",
    list: "gap-1.5",
    tab: (active) =>
      [
        "relative px-5 py-2.5 font-medium rounded-t-xl border-x border-t transition-all duration-200",
        active
          ? "bg-surface text-text_primary border-border border-b-surface shadow-[inset_0_2px_0_0_var(--color-primary)] z-10"
          : "bg-surface_alt/80 border-transparent text-text_secondary hover:text-text_primary hover:bg-surface_alt",
      ].join(" "),
    badgeActive: "bg-primary text-white",
    badgeInactive: "bg-border text-text_secondary",
    dotActive: "bg-primary",
  },

  // 5. GHOST — no chrome, subtle fill on hover/active ───────────────────────
  ghost: {
    wrapper: "",
    list: "gap-0.5",
    tab: (active) =>
      [
        "relative px-3.5 py-2 font-medium rounded-lg transition-all duration-200",
        active
          ? "text-primary bg-primary_tint"
          : "text-text_secondary hover:text-text_primary hover:bg-surface_alt/70",
      ].join(" "),
    badgeActive: "bg-primary text-white",
    badgeInactive: "bg-surface_alt text-text_secondary",
    dotActive: "bg-primary",
  },

  // 6. BUBBLE — full rounded pill with shadow on active ─────────────────────
  bubble: {
    wrapper: "py-0.5",
    list: "gap-2",
    tab: (active) =>
      [
        "relative px-4 py-1.5 font-semibold rounded-full transition-all duration-200 border",
        active
          ? "bg-primary text-white border-primary shadow-[0_2px_8px_rgba(37,99,235,0.30)]"
          : "bg-surface text-text_secondary border-border hover:border-border_strong hover:text-text_primary hover:bg-surface_alt",
      ].join(" "),
    badgeActive: "bg-white/25 text-white",
    badgeInactive: "bg-surface_alt text-text_secondary",
    dotActive: "bg-white",
  },

  // 7. CHIP — inverted chip, auto-flips in dark mode ────────────────────────
  chip: {
    wrapper: "py-1",
    list: "gap-2",
    tab: (active) =>
      [
        "relative px-3.5 py-1.5 font-medium rounded-md border transition-all duration-200",
        active
          ? "bg-text_primary text-surface border-text_primary shadow-sm"
          : "bg-surface text-text_secondary border-border hover:border-border_strong hover:text-text_primary hover:bg-surface_alt",
      ].join(" "),
    badgeActive: "bg-surface/20 text-surface",
    badgeInactive: "bg-surface_alt text-text_secondary",
    dotActive: "bg-surface",
  },

  // 8. FLUSH — full-width equal tabs, underline ─────────────────────────────
  flush: {
    wrapper: "border-b border-border",
    list: "gap-0 w-full",
    tab: (active) =>
      [
        "relative flex-1 text-center px-2 pb-3 pt-2.5 border-b-2 font-medium transition-all duration-200",
        active
          ? "border-primary text-primary"
          : "border-transparent text-text_secondary hover:text-text_primary hover:border-border_strong",
      ].join(" "),
    badgeActive: "bg-primary text-white",
    badgeInactive: "bg-surface_alt text-text_secondary",
    dotActive: "bg-primary",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SIZE SYSTEM
// ─────────────────────────────────────────────────────────────────────────────
const SIZES = {
  xs: { text: "text-xs",   icon: 12, gap: "gap-1.5" },
  sm: { text: "text-xs",   icon: 14, gap: "gap-1.5" },
  md: { text: "text-sm",   icon: 15, gap: "gap-2"   },
  lg: { text: "text-sm",   icon: 17, gap: "gap-2"   },
  xl: { text: "text-base", icon: 18, gap: "gap-2.5" },
};

// ─────────────────────────────────────────────────────────────────────────────
// BADGE
// ─────────────────────────────────────────────────────────────────────────────
const compactFormatter = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});

// badge.compact → 20, 1.5K, 2.3M ; default → capped at 99+
const formatBadgeCount = (badge) =>
  badge.compact
    ? compactFormatter.format(badge.count).toLowerCase()
    : badge.count > 99 ? "99+" : badge.count;

const Badge = ({ badge, isActive, vcfg }) => {
  if (!badge) return null;

  if (badge.type === "dot") {
    return (
      <span className="relative flex items-center justify-center w-2.5 h-2.5 ml-0.5 flex-shrink-0">
        <span
          className={[
            "absolute inline-flex h-full w-full rounded-full animate-ping opacity-60",
            isActive ? vcfg.dotActive : "bg-text_muted",
          ].join(" ")}
        />
        <span
          className={[
            "relative inline-flex rounded-full w-2 h-2",
            isActive ? vcfg.dotActive : "bg-text_muted",
          ].join(" ")}
        />
      </span>
    );
  }

  if (badge.type === "count") {
    if (!badge.count || badge.count === 0) return null;
    return (
      <span
        className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none tabular-nums flex-shrink-0 bg-danger text-white shadow-sm"
        aria-label={`${badge.count} notifications`}
      >
        {badge.count > 99 ? "99+" : badge.count}
      </span>
    );
  }

  const semanticCls = {
    success: "bg-success_bg text-success ring-1 ring-success_border",
    warning: "bg-warning_bg text-warning ring-1 ring-warning_border",
    error:   "bg-danger_bg text-danger ring-1 ring-danger_border",
  };

  if (!badge.count || badge.count === 0) return null;

  const cls = semanticCls[badge.type] ?? (isActive ? vcfg.badgeActive : vcfg.badgeInactive);

  return (
    <span
      className={[
        "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none tabular-nums flex-shrink-0 transition-colors duration-200",
        cls,
      ].join(" ")}
    >
      {formatBadgeCount(badge)}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SCROLL BUTTON
// ─────────────────────────────────────────────────────────────────────────────
const ScrollBtn = ({ direction, onClick }) => (
  <button
    onClick={onClick}
    aria-label={`Scroll tabs ${direction}`}
    className={[
      "flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full z-10",
      "bg-surface border border-border shadow-sm text-text_muted",
      "hover:text-primary hover:border-primary hover:shadow",
      "active:scale-95 transition-all duration-150",
      direction === "left" ? "mr-1.5" : "ml-1.5",
    ].join(" ")}
  >
    {direction === "left" ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL TABS — main component
// ─────────────────────────────────────────────────────────────────────────────
const GlobalTabs = ({
  tabs = [],
  activeTab: controlledActiveTab,
  defaultActiveTab = 0,
  onTabChange = () => {},
  variant = "underlined",
  size = "md",
  showScrollButtons = true,
  className = "",
}) => {
  const isControlled = controlledActiveTab !== undefined;
  const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveTab);
  const activeTab = isControlled ? controlledActiveTab : internalActiveTab;

  const [showLeft,  setShowLeft]  = useState(false);
  const [showRight, setShowRight] = useState(false);

  const containerRef = useRef(null);
  const tabRefs      = useRef([]);

  const vcfg = VARIANTS[variant] ?? VARIANTS.underlined;
  const scfg = SIZES[size]      ?? SIZES.md;
  const isFlushed = variant === "flush";

  const checkScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 2);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  };

  useEffect(() => {
    checkScroll();
    const ro = new ResizeObserver(checkScroll);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [tabs]);

  useEffect(() => {
    const wrap = containerRef.current;
    const el   = tabRefs.current[activeTab];
    if (!wrap || !el) return;
    const wr = wrap.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    if      (er.left  < wr.left)  wrap.scrollLeft -= wr.left  - er.left;
    else if (er.right > wr.right) wrap.scrollLeft += er.right - wr.right;
  }, [activeTab]);

  const handleTabClick = (index) => {
    if (!isControlled) setInternalActiveTab(index);
    onTabChange(index, tabs[index]);
  };

  const scroll = (dir) => {
    containerRef.current?.scrollBy({
      left: dir === "left" ? -200 : 200,
      behavior: "smooth",
    });
  };

  return (
    <div
      className={[
        "relative flex items-center w-full",
        vcfg.wrapper,
        className,
      ].join(" ")}
    >
      {showScrollButtons && showLeft && (
        <ScrollBtn direction="left" onClick={() => scroll("left")} />
      )}

      <div
        ref={containerRef}
        onScroll={checkScroll}
        className="flex-1 overflow-x-auto"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        <div
          role="tablist"
          className={[
            isFlushed ? "flex w-full" : "inline-flex min-w-full",
            vcfg.list,
          ].join(" ")}
        >
          {tabs.map((tab, index) => {
            const isActive = activeTab === index;
            return (
              <button
                key={tab.id ?? index}
                ref={(el) => (tabRefs.current[index] = el)}
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleTabClick(index)}
                className={[
                  vcfg.tab(isActive),
                  scfg.text,
                  isFlushed ? "flex-1" : "",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                  "whitespace-nowrap cursor-pointer select-none",
                ].join(" ")}
              >
                <span
                  className={[
                    "inline-flex items-center justify-center",
                    scfg.gap,
                  ].join(" ")}
                >
                  {tab.icon && (
                    <span
                      className="flex-shrink-0 flex items-center justify-center"
                      aria-hidden="true"
                    >
                      {React.isValidElement(tab.icon)
                        ? React.cloneElement(tab.icon, { size: scfg.icon })
                        : tab.icon}
                    </span>
                  )}

                  <span className="font-medium leading-none">{tab.label}</span>

                  <Badge badge={tab.badge} isActive={isActive} vcfg={vcfg} />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {showScrollButtons && showRight && (
        <ScrollBtn direction="right" onClick={() => scroll("right")} />
      )}
    </div>
  );
};

export default GlobalTabs;
